import { cardSx, type TonalTokens } from "../../../polish/tokens";

// The tonal full-card button (TONAL_DESIGN_SYSTEM.md §7): an empty button
// laid under a card's content opens the report; the content lets taps
// through, and each control on it (the identity, ⋯, View all) is a sibling
// above the button, never nested inside it.

export const CARD_OPEN_CLASS = "card-open";

/** Each tonal card of a report's row (its results, its notes). */
export const REPORT_PANEL_CLASS = "report-panel";

/**
 * A report's row of cards, one unit: hovering either card washes both, a
 * press scales the row, and layout is skipped while it's off screen.
 */
export const reportRowSx = (tokens: TonalTokens, intrinsicHeight: number) =>
  ({
    contentVisibility: "auto",
    containIntrinsicSize: `auto ${intrinsicHeight}px`,
    transition: "transform 150ms ease",
    [`& > .${REPORT_PANEL_CLASS}`]: {
      ...cardSx(tokens),
      transition: "background-color 120ms ease",
    },
    "@media (hover: hover)": {
      [`&:has(.${CARD_OPEN_CLASS}:hover) > .${REPORT_PANEL_CLASS}`]: {
        backgroundColor: tokens.raised,
      },
    },
    [`&:has(.${CARD_OPEN_CLASS}:active)`]: { transform: "scale(0.99)" },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      [`& > .${REPORT_PANEL_CLASS}`]: { transition: "none" },
      [`&:has(.${CARD_OPEN_CLASS}:active)`]: { transform: "none" },
    },
  }) as const;

export const cardButtonSx = (tokens: TonalTokens) =>
  ({
    all: "unset",
    position: "absolute",
    inset: 0,
    borderRadius: "12px",
    cursor: "pointer",
    WebkitTapHighlightColor: "transparent",
    "&:focus-visible": {
      outline: `2px solid ${tokens.gain}`,
      outlineOffset: "2px",
    },
  }) as const;

/** The content layer: above the button, letting taps fall through to it. */
export const passThroughSx = {
  position: "relative",
  pointerEvents: "none",
} as const;

/** A control on the content layer takes its own taps. */
export const controlSx = { pointerEvents: "auto" } as const;
